import { h, render } from "vue";
import UiIcon from "../components/ui/UiIcon.vue";

/** Perpendicular distance in coordinate units; shared by map insertion tools. */
export function pointSegmentDistance(px, py, ax, ay, bx, by) {
  const dx = bx - ax,
    dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - ax, py - ay);
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

export function getVisibleRouteWarnings(
  warnings,
  locations = [],
  sections = [],
) {
  const messages = Array.isArray(warnings)
    ? warnings.filter(
        (message) =>
          !/official-route import is experimental\.|Permit\s+Guidance\s+application|IDOT.?s\s+ITAP\s+system/i.test(
            message,
          ),
      )
    : [];
  const located = new Set(
    getLocatedExitWarnings(messages, locations, sections).map(
      (item) => item.message,
    ),
  );
  return messages.filter(
    (message) =>
      !/(?:^|: )Route retry for |^Review connecting road between permits .*retained from the calculated route|^Permit \d+(?: \(recalculated portion\))?: Road-match confidence is |^Connecting road \d+: Route match could not be verified/.test(
        message,
      ) || located.has(message),
  );
}

export function routeWarningLabel(message) {
  return message.replace(
    /Route retry for (.+?) (?:could not reconnect safely|could not verify road adherence|missed a waypoint|failed); truck route retained\./,
    "Check the highlighted route on $1 against your permit before travel.",
  );
}

export function getLocatedExitWarnings(warnings, locations, sections = []) {
  if (!Array.isArray(warnings) || !Array.isArray(locations)) return [];
  return locations
    .filter(
      (item) =>
        item &&
        warnings.includes(item.message) &&
        (item.kind !== "retry-section" ||
          (Array.isArray(item.geometry?.coordinates) &&
            item.geometry.coordinates.length >= 2 &&
            item.geometry.coordinates.every(
              (p) =>
                Array.isArray(p) &&
                Number.isFinite(p[0]) &&
                Math.abs(p[0]) <= 180 &&
                Number.isFinite(p[1]) &&
                Math.abs(p[1]) <= 90,
            ))) &&
        (item.exit ||
          [
            "start-direction",
            "approximate-endpoint",
            "road-deviation",
            "retry-section",
          ].includes(item.kind)) &&
        (!item.kind ||
          (item.sectionId &&
            Number.isInteger(item.sectionRevision) &&
            Array.isArray(sections) &&
            sections.some(
              (section) => section && section.id === item.sectionId,
            ))) &&
        Number.isFinite(item.lat) &&
        Math.abs(item.lat) <= 90 &&
        Number.isFinite(item.lng) &&
        Math.abs(item.lng) <= 180,
    )
    .flatMap((item) => {
      if (!item.kind) return [item];
      const section = sections.find(
        (section) => section?.id === item.sectionId,
      );
      if (section.revision === item.sectionRevision) return [item];
      const coordinates = section.geometry?.coordinates;
      if (
        !Array.isArray(coordinates) ||
        coordinates.length < 2 ||
        !coordinates.every(
          (p) =>
            Array.isArray(p) &&
            Number.isFinite(p[0]) &&
            Math.abs(p[0]) <= 180 &&
            Number.isFinite(p[1]) &&
            Math.abs(p[1]) <= 90,
        )
      )
        return [];
      const nearest = (target, from = 0) => {
        let best = -1,
          distance = 0.001 ** 2;
        for (let i = from; i < coordinates.length; i++) {
          const p = coordinates[i];
          const d = (p[0] - target[0]) ** 2 + (p[1] - target[1]) ** 2;
          if (d <= distance) {
            best = i;
            distance = d;
          }
        }
        return best;
      };
      if (item.kind === "retry-section") {
        const start = nearest(item.geometry.coordinates[0]);
        const end =
          start < 0 ? -1 : nearest(item.geometry.coordinates.at(-1), start + 1);
        if (end < 0) return [];
        const current = coordinates.slice(start, end + 1);
        const midpoint = current[Math.floor(current.length / 2)];
        return [
          {
            ...item,
            lat: midpoint[1],
            lng: midpoint[0],
            geometry: { type: "LineString", coordinates: current },
          },
        ];
      }
      const index = nearest([item.lng, item.lat]);
      return index < 0
        ? []
        : [{ ...item, lng: coordinates[index][0], lat: coordinates[index][1] }];
    });
}

export const SEARCH_CATEGORIES = Object.freeze([
  { key: "fuel", icon: "fuel", marker: "G", label: "Gas" },
  { key: "truck_stop", icon: "truck", marker: "T", label: "Truck Stop" },
  { key: "food", icon: "food", marker: "F", label: "Food" },
  { key: "rest_area", icon: "parking", marker: "R", label: "Rest Area" },
  { key: "hotel", icon: "bed", marker: "H", label: "Hotel" },
  { key: "parking", icon: "parking", marker: "P", label: "Parking" },
  { key: "weigh_station", icon: "weight", marker: "W", label: "Weigh Station" },
]);

export const STOP_CATEGORIES = SEARCH_CATEGORIES.filter((item) =>
  ["truck_stop", "weigh_station", "rest_area", "hotel"].includes(item.key),
);

const stopIcons = new Map();
function stopCategory(poi, key) {
  const subtype =
    poi.stopSubcategory || poi.subcategories?.[0] || poi.customLabel;
  const variants = {
    hotel: { key: "hotel", icon: "bed", label: "Hotel" },
    motel: { key: "motel", icon: "bed", label: "Motel" },
    truck_parking: {
      key: "truck_parking",
      icon: "parking",
      label: "Truck parking",
    },
    rest_area: { key: "rest_area", icon: "bed", label: "Rest area" },
    dot_weigh_station: {
      key: "dot_weigh_station",
      icon: "shield",
      label: "DOT weigh station",
    },
    cat_scale: { key: "cat_scale", icon: "weight", label: "CAT scale" },
    commercial_scale: {
      key: "commercial_scale",
      icon: "weight",
      label: "Commercial scale",
    },
  };
  return (
    variants[subtype] ||
    variants[key] ||
    SEARCH_CATEGORIES.find((item) => item.key === key)
  );
}
function stopIconHtml(category) {
  if (!stopIcons.has(category.icon)) {
    const host = document.createElement("div");
    render(h(UiIcon, { name: category.icon, size: 18 }), host);
    stopIcons.set(category.icon, host.innerHTML);
    render(null, host);
  }
  return stopIcons.get(category.icon);
}

const TRUCK_STOP_BRANDS = {
  pilot: { color: "#cc0000", abbr: "PFJ", textColor: "#ffd200" },
  "flying j": { color: "#cc0000", abbr: "PFJ", textColor: "#ffd200" },
  "pilot flying j": { color: "#cc0000", abbr: "PFJ", textColor: "#ffd200" },
  "love's": { color: "#ffd100", abbr: "L", textColor: "#e31837" },
  loves: { color: "#ffd100", abbr: "L", textColor: "#e31837" },
  "love's travel stop": {
    color: "#ffd100",
    abbr: "L",
    textColor: "#e31837",
  },
  ta: { color: "#003da5", abbr: "TA", textColor: "#fff" },
  "travelcenters of america": {
    color: "#003da5",
    abbr: "TA",
    textColor: "#fff",
  },
  petro: { color: "#d42f2f", abbr: "PT", textColor: "#fff" },
  "petro stopping center": {
    color: "#d42f2f",
    abbr: "PT",
    textColor: "#fff",
  },
  "petro stopping centers": {
    color: "#d42f2f",
    abbr: "PT",
    textColor: "#fff",
  },
  "buc-ee's": { color: "#ffc72c", abbr: "B", textColor: "#c8102e" },
  "casey's": { color: "#e31837", abbr: "CG", textColor: "#fff" },
  "casey's general store": {
    color: "#e31837",
    abbr: "CG",
    textColor: "#fff",
  },
  "kum & go": { color: "#e31837", abbr: "KG", textColor: "#fff" },
  quiktrip: { color: "#8b0000", abbr: "QT", textColor: "#fff" },
  "sapp bros": { color: "#003da5", abbr: "SB", textColor: "#fff" },
  ambest: { color: "#003da5", abbr: "AB", textColor: "#fff" },
  shell: { color: "#fbce07", abbr: "SH", textColor: "#dd1d21" },
  bp: { color: "#009639", abbr: "BP", textColor: "#ffd200" },
  chevron: { color: "#003882", abbr: "CV", textColor: "#d4232a" },
  exxon: { color: "#d42f2f", abbr: "EX", textColor: "#fff" },
  mobil: { color: "#002f87", abbr: "MB", textColor: "#e31837" },
  marathon: { color: "#003da5", abbr: "MR", textColor: "#fff" },
  speedway: { color: "#d42f2f", abbr: "SW", textColor: "#ffd200" },
  "circle k": { color: "#ee3124", abbr: "CK", textColor: "#fff" },
  wawa: { color: "#c8102e", abbr: "WA", textColor: "#ffd100" },
  sheetz: { color: "#c8102e", abbr: "SZ", textColor: "#fff" },
  "7-eleven": { color: "#008060", abbr: "7E", textColor: "#f7941e" },
  sunoco: { color: "#002f87", abbr: "SN", textColor: "#ffd200" },
  valero: { color: "#003da5", abbr: "VL", textColor: "#fff" },
  citgo: { color: "#e31837", abbr: "CT", textColor: "#fff" },
  conoco: { color: "#d42f2f", abbr: "CO", textColor: "#fff" },
  "phillips 66": { color: "#d42f2f", abbr: "P66", textColor: "#fff" },
  sinclair: { color: "#006838", abbr: "SN", textColor: "#fff" },
  "murphy usa": { color: "#003da5", abbr: "MU", textColor: "#ffd200" },
  racetrac: { color: "#f26522", abbr: "RT", textColor: "#fff" },
  maverik: { color: "#c8102e", abbr: "MV", textColor: "#fff" },
  "kwik trip": { color: "#e31837", abbr: "KT", textColor: "#fff" },
  "kwik star": { color: "#e31837", abbr: "KS", textColor: "#fff" },
};

export function escapeMapHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function getTruckStopBrand(brand) {
  if (typeof brand !== "string") return null;
  return TRUCK_STOP_BRANDS[brand.trim().toLowerCase()] || null;
}

export function getPoiKey(poi) {
  return `${Number(poi.lat).toFixed(3)},${Number(poi.lng).toFixed(3)}`;
}

export function buildAmenityLine(tags = {}) {
  const amenities = [];
  if (tags.diesel === "yes" || tags.hgv === "yes") amenities.push("Diesel");
  if (tags.def === "yes") amenities.push("DEF");
  if (tags.shower === "yes") amenities.push("Showers");
  if (tags.scales === "yes") amenities.push("Scales");
  if (tags.wifi === "yes" || tags.wifi === "wlan") amenities.push("WiFi");
  if (tags.restaurant === "yes") amenities.push("Food");
  if (tags.truckParking === "yes") amenities.push("Truck Parking");
  else if (tags.capacity) amenities.push(`${tags.capacity} spots`);
  if (tags.openingHours) amenities.push(tags.openingHours);
  return amenities.join(" · ");
}

export function getPoiMarkerPresentation(poi, categoryKey, pinned = false) {
  const brand = getTruckStopBrand(poi.tags?.brand);
  const category = SEARCH_CATEGORIES.find((item) => item.key === categoryKey);
  return {
    background: brand?.color || (pinned ? "#22c55e" : "#1e293b"),
    border: pinned ? "#22c55e" : brand ? "#fff" : "#60a5fa",
    color: brand?.textColor || "#fff",
    fontSize: brand
      ? pinned
        ? "8px"
        : "var(--font-size-caption)"
      : pinned
        ? "12px"
        : "14px",
    fontWeight: brand ? "800" : pinned ? "800" : "400",
    label: brand?.abbr || category?.marker || "P",
    size: pinned ? 36 : 32,
  };
}

export function buildPoiMarkerHtml(poi, categoryKey, pinned = false) {
  const marker = getPoiMarkerPresentation(poi, categoryKey, pinned);
  const category = stopCategory(poi, categoryKey);
  const content =
    !getTruckStopBrand(poi.tags?.brand) &&
    STOP_CATEGORIES.some((item) => item.key === categoryKey)
      ? stopIconHtml(category)
      : escapeMapHtml(marker.label);
  const pinIndicator = pinned
    ? '<span style="position:absolute;top:-4px;right:-4px;width:14px;height:14px;border-radius:50%;background:#22c55e;border:1.5px solid #0f172a;"></span>'
    : "";
  const borderWidth = pinned ? 3 : 2.5;
  const shadow = pinned
    ? "0 2px 10px rgba(34,197,94,0.5)"
    : "0 2px 8px rgba(0,0,0,0.5)";
  return `<div style="position:relative;width:${marker.size}px;height:${marker.size}px;border-radius:50%;background:${marker.background};border:${borderWidth}px solid ${marker.border};box-shadow:${shadow};display:flex;align-items:center;justify-content:center;font-size:${marker.fontSize};font-weight:${marker.fontWeight};color:${marker.color};cursor:pointer;letter-spacing:-0.5px;line-height:1;">${content}${pinIndicator}</div>`;
}

export function createPoiPopupElement(
  poi,
  {
    pinLabel = null,
    includeAddStop = false,
    unpinLabel = null,
    minWidth = 180,
  } = {},
) {
  const tags = poi.tags || {};
  const key = getPoiKey(poi);
  const amenities = buildAmenityLine(tags);
  const popup = document.createElement("div");
  popup.className = "map-popup";
  popup.style.setProperty("--map-popup-min-width", `${minWidth}px`);

  const title = document.createElement("strong");
  title.className = "map-popup__title";
  title.textContent = String(poi.name ?? "");
  popup.append(title, document.createElement("br"));

  if (tags.addr) {
    const address = document.createElement("span");
    address.className = "map-popup__meta map-popup__address";
    address.textContent = String(tags.addr);
    popup.append(address, document.createElement("br"));
  }

  const distance = document.createElement("span");
  distance.className = "map-popup__meta map-popup__distance";
  distance.textContent = `${String(poi.distanceMi ?? "")} mi from route`;
  popup.append(distance);

  if (amenities) {
    const amenityRow = document.createElement("span");
    amenityRow.className = "map-popup__amenities";
    amenityRow.textContent = amenities;
    popup.append(document.createElement("br"), amenityRow);
  }

  if (tags.phone) {
    const phone = document.createElement("a");
    phone.className = "map-popup__link";
    phone.href = `tel:${tags.phone}`;
    phone.textContent = `Phone: ${tags.phone}`;
    popup.append(document.createElement("br"), phone);
  }

  if (pinLabel || includeAddStop) {
    const actions = document.createElement("div");
    actions.className = "map-popup__actions";
    if (pinLabel) {
      const pinButton = document.createElement("button");
      pinButton.className = "map-popup__button";
      pinButton.dataset.pinKey = key;
      pinButton.textContent = pinLabel;
      actions.append(pinButton);
    }
    if (includeAddStop) {
      const addButton = document.createElement("button");
      addButton.className = "map-popup__button";
      addButton.dataset.addKey = key;
      addButton.textContent = "+ Add Stop";
      actions.append(addButton);
    }
    popup.append(document.createElement("br"), actions);
  }

  if (unpinLabel) {
    const unpinButton = document.createElement("button");
    unpinButton.className =
      "map-popup__button map-popup__button--danger map-popup__button--full";
    unpinButton.dataset.unpinKey = key;
    unpinButton.textContent = unpinLabel;
    popup.append(document.createElement("br"), unpinButton);
  }

  return popup;
}

export function setPoiPinButtonState(button, pinned, unpinnedLabel = "Pin") {
  button.textContent = pinned ? "Pinned" : unpinnedLabel;
  button.classList.toggle("is-pinned", pinned);
}

export function getWaypointName(waypoint) {
  const name =
    waypoint?.customLabel || waypoint?.label || waypoint?.road || "Waypoint";
  return (
    {
      truck_parking: "Truck parking",
      rest_area: "Rest area",
      weigh_station: "Weigh station",
      dot_weigh_station: "DOT weigh station",
      cat_scale: "CAT scale",
      commercial_scale: "Commercial scale",
      truck_stop: "Truck stop",
      hotel: "Hotel",
      motel: "Motel",
    }[name] || name
  );
}

export function getWaypointMarkerPresentation(
  waypoint,
  index,
  total,
  variant = "route",
) {
  const isStart = index === 0;
  const isEnd = index === total - 1;
  const stop = STOP_CATEGORIES.some((item) => item.key === waypoint.type)
    ? stopCategory(waypoint, waypoint.type)
    : null;
  if (stop && !isStart && !isEnd)
    return {
      color: "var(--color-primary)",
      size: 28,
      label: stop.label,
      stop,
      isStart: false,
      isEnd: false,
    };
  const hasAnnotation = Boolean(waypoint.customLabel || waypoint.comment);
  if (variant === "planner") {
    const isBridge = waypoint.type === "bridge";
    return {
      color: isBridge
        ? "#f59e0b"
        : isStart
          ? "#22c55e"
          : isEnd
            ? "#ef4444"
            : hasAnnotation
              ? "#fbbf24"
              : "#3b82f6",
      hasAnnotation: hasAnnotation && !isStart && !isEnd && !isBridge,
      isBridge,
      isEnd,
      isStart,
      isTurn: false,
      label: isBridge ? `${index}` : isStart ? "S" : isEnd ? "E" : `${index}`,
      size: isStart || isEnd ? 22 : 18,
    };
  }
  const isUser = waypoint.userAdded || waypoint.userMoved;
  const isBridge = waypoint.type === "bridge";
  const isTurn = waypoint.type === "turn" && !isStart && !isEnd && !isUser;
  return {
    color: isStart
      ? "#22c55e"
      : isEnd
        ? "#ef4444"
        : isBridge
          ? "#f59e0b"
          : isTurn
            ? "#dc2626"
            : "#3b82f6",
    hasAnnotation: hasAnnotation && !isBridge,
    isBridge,
    isEnd,
    isStart,
    isTurn,
    label: isStart ? "S" : isEnd ? "E" : isTurn ? "T" : "",
    size: isStart || isEnd ? 22 : isBridge || isTurn ? 18 : 12,
  };
}

export function buildWaypointMarkerHtml(marker, cursor = "grab") {
  if (marker.stop)
    return `<div aria-label="${escapeMapHtml(marker.label)}" style="width:${marker.size}px;height:${marker.size}px;border-radius:50%;background:${marker.color};border:2px solid white;display:flex;align-items:center;justify-content:center;color:white;cursor:${cursor};">${stopIconHtml(marker.stop)}</div>`;
  if (marker.isBridge) {
    return `<div style="position:relative;width:${marker.size}px;height:${marker.size}px;background:${marker.color};border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4);transform:rotate(45deg);display:flex;align-items:center;justify-content:center;cursor:${cursor};"><span style="transform:rotate(-45deg);color:white;font-size:var(--font-size-caption);line-height:var(--line-height-caption);font-weight:bold;">${escapeMapHtml(marker.label)}</span></div>`;
  }
  const annotationDot = marker.hasAnnotation
    ? '<span style="position:absolute;top:-3px;right:-3px;width:8px;height:8px;border-radius:50%;background:#fbbf24;border:1.5px solid #1e293b;"></span>'
    : "";
  const shadow = marker.isTurn
    ? "0 0 0 2px #dc2626, 0 2px 8px rgba(220,38,38,0.6)"
    : "0 2px 6px rgba(0,0,0,0.4)";
  return `<div style="position:relative;width:${marker.size}px;height:${marker.size}px;border-radius:50%;background:${marker.color};border:2px solid white;box-shadow:${shadow};display:flex;align-items:center;justify-content:center;color:white;font-size:var(--font-size-caption);line-height:var(--line-height-caption);font-weight:bold;cursor:${cursor};">${escapeMapHtml(marker.label)}${annotationDot}</div>`;
}

export function buildCommunityParkingMarkerHtml() {
  return `<div style="width:32px;height:32px;border-radius:50%;background:#0f766e;border:3px solid #fbbf24;box-shadow:0 2px 10px rgba(15,118,110,0.65);display:flex;align-items:center;justify-content:center;color:white;font-size:14px;font-weight:800;cursor:pointer;line-height:1;" aria-hidden="true">P</div>`;
}

export function createPoiWaypoint(poi) {
  return {
    ...(poi.subcategories?.length
      ? { stopSubcategory: poi.subcategories[0] }
      : {}),
    lat: poi.lat,
    lng: poi.lng,
    label: poi.name,
    road: poi.tags?.addr || `${poi.lat.toFixed(4)}, ${poi.lng.toFixed(4)}`,
    type: STOP_CATEGORIES.some((item) => item.key === poi.type)
      ? poi.type
      : "waypoint",
    geocoded: true,
    userAdded: true,
    customLabel: poi.name,
  };
}
